import { CollapsibleCard } from '@ctw/shared/components/containers/collapsible-card';
import {
  StreamingContent,
  type StreamingContentProps,
} from '@ctw/shared/components/containers/streaming-content';
import { notify } from '@ctw/shared/components/toast';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { fetchCanonicalCitationResource } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { isFetchableResourceType } from '@ctw/shared/utils/fhir/resource-mappings';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useRef } from 'react';

interface PatientSummaryCardProps {
  enabled: boolean;
  summary: ReturnType<typeof usePatientSummaries>['summaries'][number];
  onAllContentPresented: StreamingContentProps['onAllContentPresented'];
}
export const PatientSummaryCard = memo(
  ({ enabled, summary, onAllContentPresented }: PatientSummaryCardProps) => {
    const { ctwFetch, graphqlClient, requestContext } = useCTW();
    const { patient } = usePatientContext();
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();
    const { openResourceDrawer } = useResourceDrawer();

    // Freeze the streaming decision based on the block's status when it first appears.
    // A block that arrived as 'running' keeps streaming even after it transitions to
    // 'completed'; a block that arrived as 'completed' never streams.
    const initialStatusRef = useRef(summary.status);

    const contentBlocks = useMemo(
      () => [
        {
          tokenizationMode: 'word' as const,
          markdown: summary.content,
          streaming: initialStatusRef.current === 'running',
        },
      ],
      [summary.content],
    );

    const handleCitation = useCallback(
      (citationId: string) => {
        const citation = summary.citations?.find(
          (citation) => citation.id.toString() === citationId,
        );

        if (!citation) {
          return {
            onClick: () =>
              notify({
                type: 'error',
                title: 'Unable to load citation',
                body: 'We were unable to load this citation (code: CNF). If this problem persists, please contact support.',
              }),
          };
        }

        return {
          onClick: () => {
            if (!isFetchableResourceType(citation.resourceType)) {
              notify({
                type: 'error',
                title: 'Unable to load citation',
                body: 'We were unable to load this citation (code: IRT). If this problem persists, please contact support.',
              });
            } else {
              void fetchCanonicalCitationResource({
                resourceType: citation.resourceType,
                resourceIds: [citation.resourceId],
                telemetry,
                ctwFetch,
                patient,
                queryClient,
                graphqlClient,
                requestContext,
              }).then((resource) => {
                if (resource === null) {
                  notify({
                    type: 'error',
                    title: 'Unable to load citation',
                    body: 'We were unable to load this citation (code: FTFR). If this problem persists, please contact support.',
                  });
                } else {
                  openResourceDrawer({
                    model: resource,
                    telemetry,
                  });
                }
              });
            }
          },
        };
      },
      [
        summary.citations,
        openResourceDrawer,
        telemetry,
        ctwFetch,
        patient,
        queryClient,
        graphqlClient,
        requestContext,
      ],
    );

    return (
      <CollapsibleCard
        label={summary.title}
        controls={{ copyToClipboard: { content: summary.content } }}
        loading={!enabled}
      >
        <StreamingContent
          enabled={enabled}
          onAllContentPresented={onAllContentPresented}
          contentBlocks={contentBlocks}
          handleCitation={handleCitation}
        />
      </CollapsibleCard>
    );
  },
);
